"use client"

import { useEffect, useMemo, useState } from "react"
import api from "../../../utils/api"
import { formatDateWithTime, formatMoney } from "../../../utils/format"

const emptyDetail = () => ({
  typeOfProperty: "Residential",
  landlordPin: "",
  paymentDate: "",
  invoiceNo: "",
  invoiceDate: "",
  lrNumber: "",
  building: "",
  street: "",
  town: "",
  grossAmount: "",
  taxRate: "10",
  taxAmount: "",
})

const exampleForm = () => ({
  withholderPin: "P910150323J",
  transactionUniqueNo: `RLTEST${Date.now()}${Math.random().toString(36).slice(2, 8)}`,
  taxPeriodFrom: "2025-09-01T00:00",
  taxPeriodTo: "2025-09-30T00:00",
  transactionDetails: [
    {
      typeOfProperty: "Residential",
      landlordPin: "P590011760T",
      paymentDate: "2025-09-19T00:00",
      invoiceNo: "KRACU000000006",
      invoiceDate: "2025-09-19T00:00",
      lrNumber: "NAIROBI/BLOCK0/01",
      building: "A Tower",
      street: "Lasie Avenue",
      town: "Nairobi",
      grossAmount: "50050.00",
      taxRate: "7.5",
      taxAmount: "3754",
    },
  ],
})

const num = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const money = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100

const parseDateValue = (value) => {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const normalizeDateTime = (value) => {
  const trimmed = String(value || "").trim()
  if (!trimmed) return ""
  return trimmed.length === 16 ? `${trimmed}:00` : trimmed
}

const isKraPin = (value) => /^[A-Z][0-9]{9}[A-Z]$/i.test(String(value || "").trim())
const toFixedMoneyString = (value) => money(value).toFixed(2)

function AdminKraPanel() {
  const [form, setForm] = useState({
    withholderPin: "",
    transactionUniqueNo: "",
    taxPeriodFrom: "",
    taxPeriodTo: "",
    transactionDetails: [emptyDetail()],
  })
  const [history, setHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [tokenLoading, setTokenLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [tokenInfo, setTokenInfo] = useState(null)
  const [responseInfo, setResponseInfo] = useState(null)

  const computedDetails = useMemo(() => form.transactionDetails.map((detail) => {
    const grossAmount = money(detail.grossAmount)
    const taxRate = money(detail.taxRate)
    const taxAmount = detail.taxAmount === "" ? money((grossAmount * taxRate) / 100) : money(detail.taxAmount)
    return { ...detail, grossAmount, taxRate, taxAmount }
  }), [form.transactionDetails])

  const totals = useMemo(() => ({
    noOfTransactions: computedDetails.length,
    totalGrossAmount: money(computedDetails.reduce((sum, detail) => sum + num(detail.grossAmount), 0)),
    totalTaxAmount: money(computedDetails.reduce((sum, detail) => sum + num(detail.taxAmount), 0)),
  }), [computedDetails])

  const payload = useMemo(() => ({
    transactionHeader: {
      withholderPin: form.withholderPin.trim(),
      transactionUniqueNo: form.transactionUniqueNo.trim(),
      noOfTransactions: totals.noOfTransactions,
      taxObligation: "WHTRENT",
      taxPeriodFrom: normalizeDateTime(form.taxPeriodFrom),
      taxPeriodTo: normalizeDateTime(form.taxPeriodTo),
      totalGrossAmount: toFixedMoneyString(totals.totalGrossAmount),
      totalTaxAmount: String(Math.round(totals.totalTaxAmount)),
    },
    transactionDetails: computedDetails.map((detail) => ({
      typeOfProperty: detail.typeOfProperty,
      landlordPin: detail.landlordPin.trim(),
      paymentDate: normalizeDateTime(detail.paymentDate),
      invoiceNo: detail.invoiceNo.trim(),
      invoiceDate: normalizeDateTime(detail.invoiceDate),
      lrNumber: detail.lrNumber.trim(),
      building: detail.building.trim(),
      street: detail.street.trim(),
      town: detail.town.trim(),
      grossAmount: Number(detail.grossAmount),
      taxRate: Number(detail.taxRate),
      taxAmount: Math.round(detail.taxAmount),
    })),
  }), [computedDetails, form.taxPeriodFrom, form.taxPeriodTo, form.transactionUniqueNo, form.withholderPin, totals.noOfTransactions, totals.totalGrossAmount, totals.totalTaxAmount])

  const validationErrors = useMemo(() => {
    const errors = []
    const periodFrom = parseDateValue(payload.transactionHeader.taxPeriodFrom)
    const periodTo = parseDateValue(payload.transactionHeader.taxPeriodTo)

    if (!isKraPin(payload.transactionHeader.withholderPin)) {
      errors.push("Withholder PIN must match the KRA PIN format.")
    }

    if (periodFrom && periodTo && periodFrom > periodTo) {
      errors.push("Tax period from must be before or equal to tax period to.")
    }

    const detailsGross = money(payload.transactionDetails.reduce((sum, detail) => sum + num(detail.grossAmount), 0))
    const headerGross = money(payload.transactionHeader.totalGrossAmount)
    if (detailsGross !== headerGross) {
      errors.push("Sum of all detail gross amounts must equal the header total gross amount.")
    }

    const detailsTax = money(payload.transactionDetails.reduce((sum, detail) => sum + num(detail.taxAmount), 0))
    const headerTax = money(payload.transactionHeader.totalTaxAmount)
    if (detailsTax !== headerTax) {
      errors.push("Sum of all detail tax amounts must equal the header total tax amount.")
    }

    payload.transactionDetails.forEach((detail, index) => {
      const paymentDate = parseDateValue(detail.paymentDate)
      const invoiceDate = parseDateValue(detail.invoiceDate)
      const expectedTax = money((num(detail.grossAmount) * num(detail.taxRate)) / 100)
      const roundedExpectedTax = Math.round(expectedTax)
      const roundedActualTax = Math.round(num(detail.taxAmount))

      if (!isKraPin(detail.landlordPin)) {
        errors.push(`Detail ${index + 1}: landlord PIN must match the KRA PIN format.`)
      }

      if (periodFrom && periodTo && paymentDate && (paymentDate < periodFrom || paymentDate > periodTo)) {
        errors.push(`Detail ${index + 1}: payment date must fall within the tax period.`)
      }

      if (invoiceDate && paymentDate && invoiceDate > paymentDate) {
        errors.push(`Detail ${index + 1}: invoice date must be on or before payment date.`)
      }

      if (roundedActualTax !== roundedExpectedTax) {
        errors.push(`Detail ${index + 1}: tax amount must equal gross amount x tax rate / 100 after rounding.`)
      }
    })

    return errors
  }, [payload])

  const loadHistory = async () => {
    try {
      setHistoryLoading(true)
      const res = await api.get("/kra/rental-withholding/history?limit=8")
      setHistory(Array.isArray(res?.data?.submissions) ? res.data.submissions : [])
    } catch (error) {
      console.error("Failed to load KRA history", error)
    } finally {
      setHistoryLoading(false)
    }
  }

  useEffect(() => {
    loadHistory()
  }, [])

  const setHeader = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))
  const setDetail = (index, field, value) => setForm((prev) => ({
    ...prev,
    transactionDetails: prev.transactionDetails.map((detail, detailIndex) => detailIndex === index ? { ...detail, [field]: value } : detail),
  }))

  const addDetail = () => setForm((prev) => ({ ...prev, transactionDetails: [...prev.transactionDetails, emptyDetail()] }))
  const removeDetail = (index) => setForm((prev) => ({
    ...prev,
    transactionDetails: prev.transactionDetails.length === 1 ? prev.transactionDetails : prev.transactionDetails.filter((_, detailIndex) => detailIndex !== index),
  }))

  const loadExample = () => {
    setForm(exampleForm())
    setMessage({
      type: "success",
      text: "KRA sandbox test data loaded with a fresh transaction reference. Use sandbox credentials only.",
    })
    setTokenInfo(null)
    setResponseInfo(null)
  }

  const resetForm = () => {
    setForm({
      withholderPin: "",
      transactionUniqueNo: "",
      taxPeriodFrom: "",
      taxPeriodTo: "",
      transactionDetails: [emptyDetail()],
    })
    setMessage(null)
    setTokenInfo(null)
    setResponseInfo(null)
  }

  const checkToken = async () => {
    try {
      setTokenLoading(true)
      setMessage(null)
      const res = await api.post("/kra/rental-withholding/token-test")
      setTokenInfo(res.data)
      setMessage({ type: "success", text: "KRA sandbox token fetched successfully." })
    } catch (error) {
      setTokenInfo(null)
      setMessage({ type: "error", text: error?.response?.data?.error || error.message || "Failed to test token." })
    } finally {
      setTokenLoading(false)
    }
  }

  const submit = async (event) => {
    event.preventDefault()
    if (validationErrors.length) {
      setMessage({ type: "error", text: validationErrors[0] })
      return
    }

    try {
      setSubmitLoading(true)
      setMessage(null)
      const res = await api.post("/kra/rental-withholding/prn", payload)
      setResponseInfo(res.data)
      setMessage({ type: "success", text: res?.data?.message || "PRN generated successfully." })
    } catch (error) {
      setResponseInfo(error?.response?.data || null)
      setMessage({ type: "error", text: error?.response?.data?.error || error.message || "Failed to generate PRN." })
    } finally {
      setSubmitLoading(false)
      loadHistory()
    }
  }

  return (
    <section className="mb-6 sm:mb-8 rounded-2xl border border-gray-200 bg-white p-4 shadow-lg sm:p-6">
      <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-red-600">KRA Sandbox</p>
          <h3 className="mt-1 text-xl font-bold text-gray-900">Rental Withholding PRN</h3>
          <p className="mt-1 text-sm text-gray-600">Server-side KRA integration for super admins. Totals are computed automatically from the detail rows.</p>
          <p className="mt-1 text-xs text-gray-500">The example uses KRA sandbox test PINs and creates a fresh reference each time you load it. Use sandbox credentials only.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={loadExample} className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            Load Official Example
          </button>
          <button type="button" onClick={resetForm} className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            Clear
          </button>
          <button type="button" onClick={checkToken} disabled={tokenLoading} className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
            {tokenLoading ? "Checking..." : "Test KRA Connection"}
          </button>
        </div>
      </div>

      {message ? <div className={`mt-4 rounded-xl border px-4 py-3 text-sm ${message.type === "success" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-700"}`}>{message.text}</div> : null}

      {validationErrors.length ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="font-semibold">Validation checks</p>
          <ul className="mt-2 list-disc pl-5">
            {validationErrors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          Current form data passes the local totals, dates, tax, and PIN format checks.
        </div>
      )}

      <div className="mt-4 grid gap-4 xl:grid-cols-[1.3fr_0.9fr]">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <input value={form.withholderPin} onChange={(event) => setHeader("withholderPin", event.target.value)} placeholder="Withholder PIN" className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" required />
            <input value={form.transactionUniqueNo} onChange={(event) => setHeader("transactionUniqueNo", event.target.value)} placeholder="Transaction reference" className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" required />
            <input type="datetime-local" value={form.taxPeriodFrom} onChange={(event) => setHeader("taxPeriodFrom", event.target.value)} className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" required />
            <input type="datetime-local" value={form.taxPeriodTo} onChange={(event) => setHeader("taxPeriodTo", event.target.value)} className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" required />
          </div>

          <div className="grid gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-4 sm:grid-cols-3">
            <div><p className="text-xs uppercase tracking-wide text-gray-500">Transactions</p><p className="mt-1 text-lg font-bold text-gray-900">{totals.noOfTransactions}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-gray-500">Gross</p><p className="mt-1 text-lg font-bold text-gray-900">{formatMoney(totals.totalGrossAmount)}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-gray-500">Tax</p><p className="mt-1 text-lg font-bold text-gray-900">{formatMoney(totals.totalTaxAmount)}</p></div>
          </div>

          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-600">Transaction Details</h4>
            <button type="button" onClick={addDetail} className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700">Add Row</button>
          </div>

          {form.transactionDetails.map((detail, index) => (
            <div key={`detail-${index}`} className="rounded-2xl border border-gray-200 p-4">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-800">Entry {index + 1}</p>
                <button type="button" onClick={() => removeDetail(index)} disabled={form.transactionDetails.length === 1} className="text-xs font-semibold text-red-600 disabled:opacity-40">Remove</button>
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                <select value={detail.typeOfProperty} onChange={(event) => setDetail(index, "typeOfProperty", event.target.value)} className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm"><option value="Residential">Residential</option><option value="Commercial">Commercial</option></select>
                <input value={detail.landlordPin} onChange={(event) => setDetail(index, "landlordPin", event.target.value)} placeholder="Landlord PIN" className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" required />
                <input type="datetime-local" value={detail.paymentDate} onChange={(event) => setDetail(index, "paymentDate", event.target.value)} className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" required />
                <input value={detail.invoiceNo} onChange={(event) => setDetail(index, "invoiceNo", event.target.value)} placeholder="Invoice No" className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" required />
                <input type="datetime-local" value={detail.invoiceDate} onChange={(event) => setDetail(index, "invoiceDate", event.target.value)} className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" required />
                <input value={detail.lrNumber} onChange={(event) => setDetail(index, "lrNumber", event.target.value)} placeholder="LR Number" className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" />
                <input value={detail.building} onChange={(event) => setDetail(index, "building", event.target.value)} placeholder="Building" className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" />
                <input value={detail.street} onChange={(event) => setDetail(index, "street", event.target.value)} placeholder="Street" className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" />
                <input value={detail.town} onChange={(event) => setDetail(index, "town", event.target.value)} placeholder="Town" className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" />
                <input type="number" min="0" step="0.01" value={detail.grossAmount} onChange={(event) => setDetail(index, "grossAmount", event.target.value)} placeholder="Gross Amount" className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" required />
                <input type="number" min="0" step="0.01" value={detail.taxRate} onChange={(event) => setDetail(index, "taxRate", event.target.value)} placeholder="Tax Rate" className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" required />
                <input type="number" min="0" step="0.01" value={detail.taxAmount} onChange={(event) => setDetail(index, "taxAmount", event.target.value)} placeholder="Tax Amount" className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" />
              </div>
            </div>
          ))}

          <div className="flex justify-end">
            <button type="submit" disabled={submitLoading || validationErrors.length > 0} className="rounded-xl bg-gray-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60">{submitLoading ? "Submitting..." : "Generate PRN"}</button>
          </div>
        </form>

        <div className="space-y-4">
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-600">Connection Result</h4>
            {tokenInfo ? <div className="mt-3 space-y-2 text-sm text-gray-700"><div>Token Type: <span className="font-semibold text-gray-900">{tokenInfo.tokenType || "n/a"}</span></div><div>Expires In: <span className="font-semibold text-gray-900">{tokenInfo.expiresIn || "n/a"}</span></div><div className="break-all">Preview: <span className="font-semibold text-gray-900">{tokenInfo.tokenPreview || "n/a"}</span></div></div> : <p className="mt-3 text-sm text-gray-500">Use the token test to verify the backend KRA credentials.</p>}
          </div>

          <div className="rounded-2xl border border-gray-200 p-4">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-600">Payload Preview</h4>
            <pre className="mt-3 max-h-72 overflow-auto rounded-xl bg-gray-950 p-3 text-xs text-emerald-200">{JSON.stringify(payload, null, 2)}</pre>
          </div>

          <div className="rounded-2xl border border-gray-200 p-4">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-600">Latest Response</h4>
            {responseInfo ? <pre className="mt-3 max-h-72 overflow-auto rounded-xl bg-gray-950 p-3 text-xs text-emerald-200">{JSON.stringify(responseInfo, null, 2)}</pre> : <p className="mt-3 text-sm text-gray-500">The most recent KRA response will appear here.</p>}
          </div>

          <div className="rounded-2xl border border-gray-200 p-4">
            <div className="flex items-center justify-between"><h4 className="text-sm font-semibold uppercase tracking-wide text-gray-600">Recent History</h4>{historyLoading ? <span className="text-xs text-gray-500">Refreshing...</span> : null}</div>
            <div className="mt-3 space-y-3">
              {history.length ? history.map((entry) => (
                <div key={entry._id} className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <div className="flex items-start justify-between gap-3"><div><p className="text-sm font-semibold text-gray-900">{entry.transactionUniqueNo || "No reference"}</p><p className="text-xs text-gray-500">{formatDateWithTime(entry.createdAt)}</p></div><span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${entry.status === "success" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{entry.status}</span></div>
                  <div className="mt-2 grid gap-2 text-xs text-gray-600 sm:grid-cols-2"><div>Withholder PIN: <span className="font-semibold text-gray-800">{entry.withholderPin || "n/a"}</span></div><div>Transactions: <span className="font-semibold text-gray-800">{entry.noOfTransactions || 0}</span></div><div>Gross: <span className="font-semibold text-gray-800">{formatMoney(entry.totalGrossAmount || 0)}</span></div><div>Tax: <span className="font-semibold text-gray-800">{formatMoney(entry.totalTaxAmount || 0)}</span></div><div>PRN: <span className="font-semibold text-gray-800">{entry.prn || "n/a"}</span></div><div>By: <span className="font-semibold text-gray-800">{entry.submittedBy?.name || "System"}</span></div></div>
                  {entry.errorMessage ? <p className="mt-2 text-xs text-red-600">{entry.errorMessage}</p> : null}
                </div>
              )) : <p className="text-sm text-gray-500">No KRA submissions yet.</p>}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default AdminKraPanel
